import axiosStatic, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import {
  getCurrentApiHost,
  API_NAMESPACE,
  API_VERSION,
} from 'config/environment';
import { logger } from 'services';
import { axios } from './axios';

interface StreamingConfig extends AxiosRequestConfig {
  onChunkReceived?: (chunk: string) => void;
  onStreamingComplete?: () => void;
  onError?: (error: unknown) => void;
}

abstract class HttpClient {
  private _api: AxiosInstance;

  protected api = () => this._api;

  /**
   * this base url is used to generate download url
   * for project files.
   * these download urls will be set as href for anchor tags.
   */
  // eslint-disable-next-line class-methods-use-this -- getter returns dynamic host, no instance state
  protected get BASEURL() {
    return `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`;
  }

  public constructor() {
    this._api = axiosStatic.create({
      baseURL: this.BASEURL,
    });
    axios().subscribe((client) => {
      this._api = client;
    });
  }

  public get = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> => {
    return this.api()
      .get(url, config)
      .then((x) => x.data);
  };

  public getResponse = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<R>> => {
    return this.api()
      .get(url, config)
      .then((x) => x);
  };

  public post = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .post(url, payload, config)
      .then((x) => x.data);

  public put = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .put(url, payload, config)
      .then((x) => x.data);

  public patch = <T, B, R = T>(
    url: string,
    payload?: B,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .patch(url, payload, config)
      .then((x) => x.data);

  public delete = <T, R = T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> =>
    this.api()
      .delete(url, config)
      .then((x) => x.data);

  public stream = async <T, B, R = T>(
    url: string,
    payload?: B,
    config?: StreamingConfig,
    method: 'GET' | 'POST' = 'GET',
  ): Promise<R> => {
    try {
      // Validate parameters
      if (method === 'POST' && !payload) {
        throw new Error('Payload is required for POST requests');
      }

      const headers: Record<string, string> = {};
      const axiosHeaders = this._api.defaults.headers;

      if (axiosHeaders) {
        Object.entries(axiosHeaders).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }

      if (config?.headers) {
        Object.entries(config.headers).forEach(([key, value]) => {
          if (typeof value === 'string') {
            headers[key] = value;
          }
        });
      }

      headers['initiator-source'] = 'UI';

      if (method === 'POST' && payload && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      const responseForReturn = await fetch(
        (config?.baseURL ?? this.BASEURL) + url,
        {
          method,
          headers,
          body: payload ? JSON.stringify(payload) : undefined,
        },
      );

      if (!responseForReturn.ok) {
        throw new Error(
          `HTTP ${responseForReturn.status}: ${responseForReturn.statusText}`,
        );
      }

      const response = responseForReturn.clone();

      if (!response.body) {
        throw new Error('No response body');
      }
      const reader = response.body.getReader();

      const processStream = async (): Promise<void> => {
        try {
          const { done, value } = await reader.read();

          if (done) {
            if (config?.onStreamingComplete) {
              config.onStreamingComplete();
            }

            return;
          }

          if (config?.onChunkReceived) {
            config.onChunkReceived(new TextDecoder().decode(value));
          }

          await processStream();
        } catch (streamError) {
          logger.error('Error when processing stream', {
            error: streamError as Error,
          });
          config?.onError?.(streamError);
        }
      };

      processStream();

      return responseForReturn as unknown as R;
    } catch (e) {
      logger.error('Error when fetching with streaming', { error: e as Error });
      config?.onError?.(e);

      throw e;
    }
  };
}

export { HttpClient };
